import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { centresApi, type Centre } from '@/api/centresApi';
import { getDistanceKm } from '@/utils/centreStats';
import { CentreCard, CentreGrid } from '@/components/centres/CentreCard';
import { NEARBY_RADIUS_KM } from '@/seo/inventoryGate';

// Re-exported so existing callers keep one import for list + radius.
export { NEARBY_RADIUS_KM };

export interface CentreListProps {
  /** When set, fetch centres filtered by these category strings (CSV behaviour matches `?categories=` API). */
  categories?: string[];
  /** One catalog service slug (matches `?service=` on the API). */
  service?: string;
  /** Any of these catalog service slugs (matches `?services=` on the API). */
  services?: string[];
  /** Optional locality lat/lng - sorts results by distance from this point. */
  near?: { lat: number; lng: number };
  /** When set with `near`, drop centres farther than this many km (metro cap). */
  radiusKm?: number;
  /** Show per-card distance. Off when `near` is only a city-level guess. */
  showDistance?: boolean;
  /** Cap displayed results. Default 12 - one full 3-column grid. */
  limit?: number;
  /** Empty state copy. */
  emptyMessage?: string;
  /** Section heading. */
  heading?: string;
  /** Sub-heading under the section title. */
  subheading?: string;
  /** Builds the link for each card. Defaults to the clinic page. */
  linkFor?: (centre: Centre) => string | undefined;
  /** Shown under the grid when there are more clinics than fit. */
  moreHref?: string;
  moreLabel?: string;
  /**
   * Called with the final (radius-filtered) centres after a successful fetch.
   * Lets a location page inventory-gate its own indexation (noindex when 0).
   * Not called on load/error, so the page stays indexable on transient failures.
   */
  onResults?: (centres: Centre[]) => void;
}

function SectionHead({
  heading,
  subheading,
  count,
  total,
  sortedByDistance,
}: {
  heading?: string;
  subheading?: string;
  count?: number;
  /** Total matching clinics when the list was cut at its cap; undefined otherwise. */
  total?: number;
  /** Whether results are distance-sorted around a locality. */
  sortedByDistance?: boolean;
}) {
  if (!heading) return null;
  return (
    <div className="mb-4">
      <h2 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">{heading}</h2>
      {subheading && <p className="mt-1 text-sm text-muted-foreground">{subheading}</p>}
      {typeof count === 'number' && count > 0 && (
        <p className="mt-1 text-xs text-muted-foreground">
          {total != null
            ? sortedByDistance
              ? `Showing the ${count} closest clinics`
              : `Showing ${count} of ${total} verified clinics`
            : sortedByDistance
              ? `${count} ${count === 1 ? 'clinic' : 'clinics'} nearby`
              : `${count} verified ${count === 1 ? 'clinic' : 'clinics'}`}
        </p>
      )}
    </div>
  );
}

/**
 * Locality-filtered clinic grid used on city / sector / condition / recovery
 * pages. Renders the same card as the /centres directory so the two surfaces
 * always look the same.
 */
export function CentreList({
  categories,
  service,
  services,
  near,
  radiusKm,
  showDistance = true,
  limit = 12,
  emptyMessage = 'No Recovry-verified clinics in this category yet.',
  heading,
  subheading,
  linkFor,
  moreHref,
  moreLabel,
  onResults,
}: CentreListProps) {
  const [centres, setCentres] = useState<Centre[]>([]);
  // Total matching clinics when the API returned a full page and reports more.
  // With a locality the API sorts nearest-first, so those extras may be nearby.
  const [overflowTotal, setOverflowTotal] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Callers often pass an inline fn; keep it in a ref so it isn't an effect
  // dependency (which would refetch on every render). Assigned inside an
  // effect, never during render.
  const onResultsRef = useRef(onResults);
  useEffect(() => {
    onResultsRef.current = onResults;
  }, [onResults]);

  const categoryKey = JSON.stringify(categories);
  const servicesKey = JSON.stringify(services);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    centresApi
      .list({
        categories,
        service,
        services,
        limit,
        ...(near ? { lat: near.lat, lng: near.lng } : {}),
      })
      .then((res) => {
        if (cancelled) return;
        let list = res.data.centres ?? [];
        // Metro cap: keep clinics within radiusKm of the locality. Use the
        // centre's own coords when present, else the API's distance_km. A centre
        // is dropped only when BOTH signals are missing or both exceed the cap —
        // so a missing field never wrongly empties the list (false noindex).
        if (near && radiusKm) {
          list = list.filter((c) => {
            const byCoords = c.location
              ? getDistanceKm(near.lat, near.lng, c.location.lat, c.location.lng)
              : null;
            const byApi = typeof c.distance_km === 'number' ? c.distance_km : null;
            const dist = byCoords ?? byApi;
            return dist != null && dist <= radiusKm;
          });
        }
        setCentres(list);
        const total = res.data.total ?? 0;
        setOverflowTotal(list.length === limit && total > limit ? total : undefined);
        onResultsRef.current?.(list);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey, service, servicesKey, near?.lat, near?.lng, radiusKm, limit]);

  if (loading) {
    return (
      <section data-loading>
        <SectionHead heading={heading} subheading={subheading} />
        <CentreGrid>
          {[...Array(3)].map((_, i) => (
            <div key={i} className="overflow-hidden rounded-2xl border bg-card shadow-sm">
              <div className="aspect-[3/2] w-full animate-pulse bg-muted/60" />
              <div className="space-y-2 p-4">
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted/60" />
                <div className="h-3 w-full animate-pulse rounded bg-muted/40" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted/40" />
              </div>
            </div>
          ))}
        </CentreGrid>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <SectionHead heading={heading} subheading={subheading} />
        <div
          data-load-error
          className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive"
        >
          We couldn&apos;t load clinics right now. Please refresh in a moment.
        </div>
      </section>
    );
  }

  if (centres.length === 0) {
    return (
      <section>
        <SectionHead heading={heading} subheading={subheading} />
        <div className="rounded-2xl border border-dashed bg-muted/30 p-8 text-center">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
          <Link
            to="/centres"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          >
            Browse all clinics <ArrowRight className="size-3.5" />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section>
      <SectionHead
        heading={heading}
        subheading={subheading}
        count={centres.length}
        total={overflowTotal}
        sortedByDistance={!!near}
      />
      <CentreGrid>
        {centres.map((centre) => {
          const distance = !showDistance
            ? null
            : near && centre.location
              ? getDistanceKm(near.lat, near.lng, centre.location.lat, centre.location.lng)
              : centre.distance_km ?? null;
          return (
            <CentreCard
              key={centre.id}
              centre={centre}
              distance={distance}
              to={linkFor?.(centre)}
            />
          );
        })}
      </CentreGrid>
      {moreHref && (
        <Link
          to={moreHref}
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          {moreLabel ?? (overflowTotal ? `See all ${overflowTotal} clinics` : 'Browse the full directory')} <ArrowRight className="size-3.5" />
        </Link>
      )}
    </section>
  );
}
